import React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { action } from 'mobx';
import { FormField } from '../../../global/form/form-field/form-field';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import { UserFormVM } from '../user-form.viewmodel';
import { SPACE_IN_BEGIN_REG_EXP } from '../../../../constants/regex.constants';
import DefaultButton from '../../../global/default-button/default-button';
import { UserAction } from '../../../../api/users.dao';
import OptionsButton, { IButtonOption } from '../../../global/options-button/options-button';

import './user-form-presentation.scss';
import { MultiSelectButton } from '../../../global/multi-select/multi-select-button';
import { CopyButton } from '../../../global/copy-button/copy-button';
import Text from '../../../global/text/text';
import { MultiSelectOption } from '../../../global/multi-select-list/_multi-select-option';
import { OEToggle } from '../../../global/oe-toggle/oe-toggle';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { ADD_TEXT, addText, APPLY_TEXT, CANCEL_TEXT, cancelText, REMOVE_TEXT } from '../../../../constants/common-labels.constants';

interface IProps {
    vm: UserFormVM;
    intl: IntlShape;
}

const UserFormPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const { intl, vm } = props;
    const formDataVM = vm.getFormDataVM();
    const isEdit = !!vm.username;

    const isDisabled = !formDataVM?.active || formDataVM?.initialLockedSystemUser;
    const isDisabledApplyButton = vm.isSaving()
        || !formDataVM?.active
        || (formDataVM?.initialLockedSystemUser && !hasPermission(Permission.SYSTEM_USER_MANAGEMENT));

    const formSubmitHandler = () => {
        const defineFormType = isEdit ? UserAction.Edit : UserAction.Create;
        return vm.handleSubmit(intl, defineFormType);
    };

    const userOptions: IButtonOption[] = [
        isEdit && {
            label: formDataVM?.active
            ? intl.formatMessage({
                id: 'oe.users.inactivateUser',
                description:'Button text',
                defaultMessage:'Inactivate User'
            }) : intl.formatMessage({
                id: 'oe.users.activateUser',
                description:'Button text',
                defaultMessage:'Activate User'
            }),
            action: vm.onActivateUser,
            danger: true,
            disabled: formDataVM?.initialLockedSystemUser
        },
        isEdit && {
            label: 'Delete User',
            action: vm.onDeleteUser,
            danger: true,
            disabled: formDataVM?.initialLockedSystemUser
        }
    ].filter(Boolean);

    const AccountSelectorMessage = () => (
        <div className='user-form__accounts-selector__message'>
            {formDataVM.isAllAccounts ? (
                <FormattedMessage
                    id='oe.accounts.fullAccess'
                    description='Placeholder text'
                    defaultMessage='User has access to all Accounts'
                />
            ) : (
                <FormattedMessage
                    id='oe.accounts.hasNoAccess'
                    description='Placeholder text'
                    defaultMessage='User has no access to any Accounts'
                />
            )}
        </div>
    );

    if (vm.isLoading) {
        return <CommonPopupBodyFooterLayout
            body={
                <div className='oe-form meeting-form'>
                    <FormattedMessage
                        description='Label text'
                        defaultMessage='Loading user data{suffix}'
                        id='oe.users.loadingData'
                        values={{
                            suffix: '...'
                        }}
                    />
                </div>
            }
        />;
    }

    return (
        <CommonPopupBodyFooterLayout
            body={
                <div className='user-form'>
                    <div className='oe-form'>
                        {<div className='oe-form__body'>
                            <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.user.firstName',
                                        description: 'User first name title',
                                        defaultMessage: 'First Name',
                                    })
                                }>
                                <input
                                    type='text'
                                    className='oe-form-field__control'
                                    value={formDataVM.firstName}
                                    onChange={({ target: { value } }) => formDataVM.setFirstName(value.replace(SPACE_IN_BEGIN_REG_EXP, ''))}
                                    disabled={isDisabled}
                                />
                                {formDataVM.showValidationErrors() ? <div className='oe-form-field__error'>{formDataVM.getFirstNameError(intl)}</div> : null}
                            </FormField>
                            <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.user.lastName',
                                        description: 'User last name title',
                                        defaultMessage: 'Last Name',
                                    })
                                }>
                                <input
                                    type='text'
                                    className='oe-form-field__control'
                                    value={formDataVM.lastName}
                                    onChange={({ target: { value } }) => formDataVM.setLastName(value.replace(SPACE_IN_BEGIN_REG_EXP, ''))}
                                    disabled={isDisabled}
                                />
                                {formDataVM.showValidationErrors() ? <div className='oe-form-field__error'>{formDataVM.getLastNameError(intl)}</div> : null}
                            </FormField>
                            <FormField
                                label={intl.formatMessage(
                                    {
                                        id: 'oe.user.email',
                                        description: 'User email title',
                                        defaultMessage: 'Email',
                                    })
                                }>
                                <input
                                    type='text'
                                    className='oe-form-field__control'
                                    value={formDataVM.email}
                                    onChange={({ target: { value } }) => formDataVM.setEmail(value.replace(SPACE_IN_BEGIN_REG_EXP, ''))}
                                    onBlur={action(() => { formDataVM.isMailTouched = true; })}
                                    disabled={isDisabled}
                                />
                                {<span className='oe-form-field__error'>
                                    {formDataVM.shouldShowEmailValidationError(intl) && formDataVM.getEmailError(intl)}
                                </span>}
                            </FormField>
                            { !formDataVM.tempPassword && (
                                <div className='user-form__generate-password-block'>
                                    <DefaultButton
                                        text={isEdit ? intl.formatMessage({
                                            id: 'oe.user.resetPassword',
                                            description: 'Button label',
                                            defaultMessage: 'Reset Password',
                                        }) : intl.formatMessage({
                                            id: 'oe.user.generatePassword',
                                            description: 'Button label',
                                            defaultMessage: 'Generate password',
                                        })}
                                        className='user-form__generate-password-button'
                                        onClick={() => formDataVM.generatePassword()}
                                        disabled={isDisabled}
                                    />
                                    {formDataVM.showValidationErrors() ? <div className='oe-form-field__error'>{formDataVM.getPasswordError(intl)}</div> : null}
                                </div>
                            )}
                            { formDataVM.tempPassword &&
                                <FormField
                                    label={intl.formatMessage({
                                            id: 'oe.common.password',
                                            description: 'Password title',
                                            defaultMessage: 'Password',
                                        })}
                                >
                                    <div className='oe-form-field__control-with-button'>
                                        <input
                                            type='text'
                                            className='oe-form-field__control user-form__generate-password-input'
                                            defaultValue={formDataVM.tempPassword}
                                            disabled
                                        />
                                        <CopyButton copyValue={formDataVM.tempPassword} />
                                    </div>
                                </FormField>
                            }
                            {formDataVM.serverErrorMessage && (
                                <div className='oe-form-field__error oe-form-field__server-error'>
                                    {formDataVM.serverErrorMessage}
                                </div>
                            )}
                            { formDataVM.tempPassword && !isEdit &&
                                <FormField>
                                    <div className='oe-form-field row center font-size-12 m-bottom-0px'>
                                        <OEToggle
                                            isSelected={formDataVM.confirmationEmailPasswordTrigger}
                                            onClick={formDataVM.toggleConfirmationEmailPassword}
                                            isDisabled={isDisabled}
                                        />
                                        <Text
                                            id={`${formDataVM.confirmationEmailPasswordTrigger
                                                ? 'oe.attendee.sendPasswordEmail'
                                                : 'oe.attendee.notSendPasswordEmail'}`
                                            }
                                            message={`${formDataVM.confirmationEmailPasswordTrigger
                                                ? 'Send Password Email'
                                                : 'Password email will not be send'}`
                                            }
                                        />
                                    </div>
                                    {!isEdit &&
                                        <span className='oe-form-field__info m-bottom-5px'>
                                            <FormattedMessage id='oe.users.newUserPasswordEmail.info' />
                                        </span>
                                    }
                                </FormField>
                            }
                            {formDataVM.tempPassword && isEdit &&
                                <span className='oe-form-field__info m-bottom-5px'>
                                    <FormattedMessage id='oe.users.editUserPasswordEmail.info' />
                                </span>
                            }
                            <FormField>
                                <div className='oe-form-field row center font-size-12 m-bottom-0px'>
                                    <Text message= {`User is ${formDataVM.active ? 'Active' : 'Inactive'}`} />
                                </div>
                            </FormField>
                            { isEdit &&
                                <FormField>
                                   <div
                                        className='oe-form-field row center font-size-12 m-bottom-0px oe-form-field__system-user'
                                        title={intl.formatMessage({
                                            id: 'oe.user.lockedSystemUserTooltip',
                                            description: 'Form field tooltip',
                                            defaultMessage: 'System Users cannot be deleted or modified except by Users with the \'System User Management\' permission.  This is to prevent critical user accounts being accidentally removed or modified.'
                                        })}
                                    >
                                       <OEToggle
                                           isSelected={formDataVM.lockedSystemUser}
                                           onClick={formDataVM.toggleLockedSystemUser}
                                           isDisabled={!formDataVM.active || !hasPermission(Permission.SYSTEM_USER_MANAGEMENT)}
                                       />
                                       <Text
                                           id='oe.user.lockedSystemUser'
                                           message='Locked System User'
                                       />
                                   </div>
                               </FormField>
                            }
                            <FormField
                                className='oe-form-field__system-role'
                                label={intl.formatMessage({
                                        id: 'oe.role.system',
                                        description: 'Form field label',
                                        defaultMessage: 'System Role',
                                    })}
                            >
                                <MultiSelectOption
                                    name='Superuser'
                                    isSelected={formDataVM.isSuperuser}
                                    onClick={() => formDataVM.setSuperUser(!formDataVM.isSuperuser)}
                                    isCheckboxInLeft
                                    isDisabled={isDisabled}
                                />
                                <MultiSelectOption
                                    name={intl.formatMessage({
                                        id: 'oe.role.openExchangeStaff',
                                        description: 'System Role',
                                        defaultMessage: 'OpenExchange Staff member',
                                    })}
                                    isSelected={formDataVM.isOpenExchangeStaffMember}
                                    onClick={() => formDataVM.setIsOpenExchangeStaffMember(!formDataVM.isOpenExchangeStaffMember)}
                                    isCheckboxInLeft
                                    isDisabled={isDisabled}
                                />
                            </FormField>
                            { !formDataVM.isSuperuser && (
                            <>
	                            <div className='user-form__accounts-selector'>
	                                <div className='user-form__accounts-selector__title'>
	                                    <FormattedMessage
	                                        id='oe.accounts.assigned'
	                                        description='Title text'
	                                        defaultMessage='Assigned Accounts'
	                                    />
                                        <MultiSelectOption
                                            name='All Accounts'
                                            isSelected={formDataVM.isAllAccounts}
                                            onClick={() => formDataVM.setIsAllAccounts(!formDataVM.isAllAccounts)}
                                            isDisabled={isDisabled}
                                        />
	                                </div>
                                    <div className='user-form__accounts-selector__selected-items'>
                                        { !formDataVM.isAllAccounts && !!vm.userFormDataVM.assignedAccounts.length ?
                                            vm.userFormDataVM.assignedAccounts
                                                .slice()
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map(account => (
                                                    <div key={account.accountId} className='user-form__accounts-selector__selected-item'>
                                                        <Text
                                                            className='user-form__accounts-selector__selected-item-label'
                                                            message={account.name}
                                                            withEllipsis
                                                        />
                                                        <DefaultButton
                                                            text={<FormattedMessage {...REMOVE_TEXT} />}
                                                            modifier='remove'
                                                            onClick={() => vm.userFormDataVM.removeAssignedAccount(account.accountId)}
                                                            className='user-form__accounts-selector__remove-item-button'
                                                            disabled={isDisabled}
                                                        />
                                                    </div>
                                                )) : <AccountSelectorMessage />
                                        }
                                    </div>
                                    {!formDataVM.isAllAccounts && (
                                        <MultiSelectButton
                                            className='user-form__accounts-selector__add-button'
                                            items={vm.userFormDataVM.selectableAccounts}
                                            disabled={[]}
                                            value={vm.userFormDataVM.assignedAccounts}
                                            keyProperty='accountId'
                                            nameProperty='name'
                                            filterHandler={vm.userFormDataVM.searchAccounts}
                                            text={
                                                <FormattedMessage
                                                    id='oe.accounts.add'
                                                    description='Button text'
                                                    defaultMessage='{prefix}Add Accounts'
                                                    values={{
                                                        prefix: '+ '
                                                    }}
                                                />
                                            }
                                            applyButtonText={addText()}
                                            resetButtonText={cancelText()}
                                            onChange={vm.userFormDataVM.setAssignedAccounts}
                                            onClick={vm.userFormDataVM.fetchAccountsList}
                                            disabledButton={isDisabled}
                                        />
                                    )}
	                            </div>
	                            <div className='user-form__roles-selector'>
	                                <div className='user-form__roles-selector__title'>
	                                    <FormattedMessage
	                                        id='oe.roles.assigned'
	                                        description='Title text'
	                                        defaultMessage='Assigned Roles'
	                                    />
	                                </div>
	                                <div className='user-form__roles-selector__selected-items'>
	                                    {!vm.userFormDataVM.assignedRoles.length ?
	                                        <div className='user-form__roles-selector__message'>
	                                            <FormattedMessage
	                                                id='oe.user.noRoles'
	                                                description='Placeholder text'
	                                                defaultMessage='User has no assigned roles'
	                                            />
	                                        </div> :
	                                        vm.userFormDataVM.assignedRoles
	                                            .slice()
	                                            .sort((a, b) => a.name.localeCompare(b.name))
	                                            .map(role => (
	                                                <div key={role.linkName} className='user-form__roles-selector__role-item'>
	                                                    <Text className='user-form__roles-selector__item-label' message={role.name} withEllipsis />
	                                                    <DefaultButton
	                                                        text={<FormattedMessage {...REMOVE_TEXT}/>}
                                                            modifier='remove'
	                                                        onClick={() => vm.userFormDataVM.removeRole(role.linkName)}
	                                                        className='user-form__roles-selector__remove-item-button'
                                                            disabled={isDisabled}
	                                                    />
	                                                </div>
	                                            ))}
	                                </div>
                                    <MultiSelectButton
                                        className='user-form__roles-selector__add-button'
                                        items={vm.userFormDataVM.selectableRoles}
                                        disabled={[]}
                                        value={vm.userFormDataVM.assignedRoles}
                                        keyProperty='linkName'
                                        nameProperty='name'
                                        filterHandler={vm.userFormDataVM.searchRoles}
                                        text={
                                            <FormattedMessage
                                                id='oe.roles.add'
                                                description='Button text'
                                                defaultMessage='{prefix}Add Roles'
                                                values={{
                                                    prefix: '+ '
                                                }}
                                            />
                                        }
                                        applyButtonText={addText()}
                                        resetButtonText={cancelText()}
                                        onChange={vm.userFormDataVM.setAssignedRoles}
                                        onClick={vm.userFormDataVM.fetchRolesList}
                                        disabledButton={isDisabled}
                                    />
	                            </div>

                                <div className='user-form__account-groups-selector'>
	                                <div className='user-form__account-groups-selector__title'>
	                                    <FormattedMessage
	                                        id='oe.user.assignedAccountGroups'
	                                        description='Title text'
	                                        defaultMessage='Assigned Account Groups'
	                                    />
	                                </div>
	                                <div className='user-form__account-groups-selector__selected-items'>
	                                    {!vm.userFormDataVM.assignedAccountGroups.length ?
	                                        <div className='user-form__account-groups-selector__message'>
	                                            <FormattedMessage
	                                                id='oe.user.noAssignedAccountGroups'
	                                                description='Placeholder text'
	                                                defaultMessage='User has no assigned account groups'
	                                            />
	                                        </div> :
	                                        vm.userFormDataVM.assignedAccountGroups
	                                            .slice()
	                                            .sort((a, b) => a.name.localeCompare(b.name))
	                                            .map((assignedAccountGroups, index) => (
	                                                <div key={index} className='user-form__account-groups-selector__account-group-item'>
	                                                    <Text className='user-form__account-groups-selector__item-label' message={assignedAccountGroups.name} withEllipsis />
	                                                    <DefaultButton
	                                                        text={<FormattedMessage {...REMOVE_TEXT} />}
                                                            modifier='remove'
	                                                        onClick={() => {
                                                                vm.userFormDataVM.removeAssignedAccountGroups(assignedAccountGroups.accountGroupId);
                                                            }}
	                                                        className='user-form__account-groups-selector__remove-item-button'
                                                            disabled={isDisabled}
	                                                    />
	                                                </div>
	                                            ))}
	                                </div>
                                    <MultiSelectButton
                                        className='user-form__account-groups-selector__add-button'
                                        items={vm.userFormDataVM.selectableAccountGroups}
                                        disabled={[]}
                                        value={vm.userFormDataVM.assignedAccountGroups}
                                        keyProperty='accountGroupId'
                                        nameProperty='name'
                                        filterHandler={vm.userFormDataVM.searchAccountGroups}
                                        text={
                                            <FormattedMessage
                                                id='oe.user.addAccountGroups'
                                                description='Button text'
                                                defaultMessage='{prefix}Add Account Groups'
                                                values={{
                                                    prefix: '+ '
                                                }}
                                            />
                                        }
                                        applyButtonText={addText()}
                                        resetButtonText={cancelText()}
                                        onChange={vm.userFormDataVM.setAssignedAccountGroups}
                                        onClick={vm.userFormDataVM.fetchAccountGroups}
                                        disabledButton={isDisabled}
                                    />
	                            </div>
                            </>)}
                        </div>
                        }
                    </div>
                </div>
            }
            footer={
                <>
                    {vm.isSaving() &&
                        <span className='oe-popup-layout__footer-status-area'>
                            {intl.formatMessage({
                                id: 'oe.user.userIsBeingSaved',
                                description: 'Placeholder about saving user',
                                defaultMessage: 'User is being saved, please wait...',
                            })}
                        </span>
                    }
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(isEdit ? APPLY_TEXT : ADD_TEXT)}
                            modifier={['fat', 'confirm-safe']}
                            onClick={() => formDataVM.active ? formSubmitHandler() : undefined}
                            disabled={isDisabledApplyButton}
                        />
                        <DefaultCloseSubWindowButton
                            text={intl.formatMessage(CANCEL_TEXT)}
                        />
                        {!!userOptions.length && <OptionsButton
                            className='options-button--right-margin-auto'
                            modifier={['fat', 'options']}
                            label='More...'
                            options={userOptions}
                            position='top'
                            align='start'
                        />}
                    </div>
                </>
            }
        />
    );

}));

export { UserFormPresentation };
